import React, { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { typeBadge } from '../hygiene/types';
import Avatar from '../epics/Avatar';
import JiraKeyChip from '../JiraKeyChip';
import RoleChip from './RoleChip';
import StatusPill from './StatusPill';
import { HandoffPerson, HandoffRow } from './types';
import * as fmt from '../../lib/format';

interface Props {
  rows: HandoffRow[];
  people: HandoffPerson[];
  loading: boolean;
  emptyText: string;
}

// One ticket walked through several statuses in a row by the same person shows as one line.
interface Chain {
  id: string;
  row: HandoffRow;
  steps: string[];
  count: number;
  at: string;
  startedAt: number;
  owners: string[];
}

const CHAIN_GAP_MS = 10 * 60_000;

// Rows arrive newest first. Walk them oldest first so each move can continue the one before it.
function toChains(rows: HandoffRow[]): Chain[] {
  const open = new Map<string, Chain & { lastTo: string; lastAt: number }>();
  const chains: Chain[] = [];
  for (const r of rows.slice().reverse()) {
    const t = new Date(r.at).getTime();
    const cur = open.get(r.issue_key);
    if (cur && cur.lastTo === r.from && t - cur.lastAt <= CHAIN_GAP_MS) {
      cur.steps.push(r.to);
      cur.count += 1;
      cur.at = r.at;
      cur.lastTo = r.to;
      cur.lastAt = t;
      for (const o of r.owners) if (!cur.owners.includes(o)) cur.owners.push(o);
    } else {
      const chain = { id: r.id, row: r, steps: [r.from, r.to], count: 1, at: r.at, startedAt: t, owners: [...r.owners], lastTo: r.to, lastAt: t };
      open.set(r.issue_key, chain);
      chains.push(chain);
    }
  }
  return chains.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

const minutesLabel = (ms: number) => (ms < 60_000 ? 'under a minute' : `${Math.round(ms / 60_000)} min`);

// Moves made outside the usual owner, grouped by the person who made them. Each row says what moved,
// from where to where, and who usually moves a ticket into that status.
export default function HandoffList({ rows, people, loading, emptyText }: Props) {
  const groups = useMemo(() => {
    const byActor = new Map<string, HandoffRow[]>();
    for (const r of rows) {
      const list = byActor.get(r.actor.account_id) ?? [];
      list.push(r);
      byActor.set(r.actor.account_id, list);
    }
    const order = new Map(people.map((p, i) => [p.account_id, i] as const));
    return Array.from(byActor, ([id, list]) => ({
      id,
      rows: list,
      chains: toChains(list),
      person: people.find((p) => p.account_id === id) ?? null,
      name: list[0].actor.name ?? id,
      roles: list[0].actor.roles,
    })).sort((a, b) => (order.get(a.id) ?? 1e9) - (order.get(b.id) ?? 1e9));
  }, [rows, people]);

  if (loading) {
    return (
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400" role="status" aria-label="Loading moves">
        {[0, 1, 2].map((i) => (
          <div key={i} className="animate-pulse border-t border-gray-100 px-5 py-4 first:border-t-0 motion-reduce:animate-none">
            <div className="h-3 w-24 rounded bg-gray-100" />
            <div className="mt-2 h-4 w-3/4 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white px-5 py-14 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">{emptyText}</div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
      {groups.map((g) => (
        <section key={g.id} aria-label={g.name}>
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 bg-gray-50/95 px-5 py-2.5 backdrop-blur first:border-t-0">
            <Avatar name={g.name} />
            <span className="text-sm font-semibold text-gray-900">{g.name}</span>
            <span className="flex flex-wrap gap-1">{g.roles.length > 0 ? g.roles.map((r) => <RoleChip key={r} role={r} />) : null}</span>
            {g.person && (
              <span className="ml-auto text-xs tabular-nums text-gray-500">
                {g.person.outside} of {g.person.checked} moves outside
                {g.person.adherence_pct !== null ? ` · ${fmt.percent(g.person.adherence_pct)} by the usual owner` : ''}
              </span>
            )}
          </div>
          <ul className="divide-y divide-gray-100">
            {g.chains.map((c) => {
              const r = c.row;
              return (
                <li key={c.id} className="px-5 py-3.5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <JiraKeyChip issueKey={r.issue_key} url={r.jira_url} />
                        {r.issue_type && (
                          <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(r.issue_type)}`}>{r.issue_type}</span>
                        )}
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-900" title={r.issue_name ?? undefined}>
                        {r.issue_name || 'Untitled'}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                        {c.steps.map((s, i) => (
                          <React.Fragment key={`${s}-${i}`}>
                            {i > 0 && <ArrowRight className="h-3.5 w-3.5 text-gray-500" aria-hidden />}
                            <StatusPill status={s} />
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="mt-1.5 text-xs text-gray-500" title={r.hint}>
                        {c.count > 1 ? `${c.count} moves in ${minutesLabel(new Date(c.at).getTime() - c.startedAt)}. ` : ''}
                        Usually moved by {c.owners.join(', ').replace(/, ([^,]*)$/, ' or $1')}
                      </div>
                    </div>
                    <time className="shrink-0 whitespace-nowrap text-xs tabular-nums text-gray-500" dateTime={c.at}>
                      {fmt.dateTime(c.at)}
                    </time>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
